import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getMobileThemePreference, saveMobileThemePreference, } from "./api/mobileThemeApi";
export const MOBILE_THEME_OPTIONS = [
    { id: "black", labelKey: "settings.appearance.videoCallBackgroundDefault", swatch: "#8D959C" },
    { id: "cocoa", labelKey: "settings.appearance.videoCallBackgroundBrown", swatch: "#C8B8AF" },
    { id: "midnight-blue", labelKey: "settings.appearance.videoCallBackgroundBlue", swatch: "#B8C4D6" },
];
const THEME_STORAGE_KEY = "dw_mobile_appearance_theme";
const CUSTOM_COLOR_STORAGE_KEY = "dw_mobile_appearance_custom_color";
const DEFAULT_CUSTOM_COLOR = "#1F2233";
function readStorage(key) {
    try {
        return localStorage.getItem(key);
    }
    catch {
        return null;
    }
}
function writeStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    }
    catch {
        // Private browsing and storage-quota errors should not block appearance changes.
    }
}
function isHexColor(value) {
    return /^#[0-9a-f]{6}$/i.test(value);
}
function clampCustomColor(value) {
    if (!isHexColor(value))
        return DEFAULT_CUSTOM_COLOR;
    const red = parseInt(value.slice(1, 3), 16);
    const green = parseInt(value.slice(3, 5), 16);
    const blue = parseInt(value.slice(5, 7), 16);
    const perceivedLightness = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
    // Keep the custom background dark enough for the fixed white mobile typography.
    if (perceivedLightness <= 0.34)
        return value.toUpperCase();
    const scale = 0.34 / perceivedLightness;
    return `#${[red, green, blue]
        .map((channel) => Math.round(channel * scale).toString(16).padStart(2, "0"))
        .join("")}`.toUpperCase();
}
function readTheme() {
    const stored = readStorage(THEME_STORAGE_KEY);
    if (stored === "black" || stored === "cocoa" || stored === "midnight-blue" || stored === "custom") {
        return stored;
    }
    return "black";
}
function readCustomColor() {
    return clampCustomColor(readStorage(CUSTOM_COLOR_STORAGE_KEY) || DEFAULT_CUSTOM_COLOR);
}
const MobileThemeContext = createContext(null);
export const MobileThemeProvider = ({ children, walletAddress, deviceSessionToken, }) => {
    const [theme, setTheme] = useState(readTheme);
    const [customColor, setCustomColorState] = useState(readCustomColor);
    const currentRef = useRef({ theme, customColor });
    const localRevisionRef = useRef(0);
    const saveTimerRef = useRef(null);
    const queueServerSave = useCallback((preference) => {
        if (!walletAddress || !deviceSessionToken)
            return;
        if (saveTimerRef.current)
            clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            void saveMobileThemePreference(deviceSessionToken, preference).catch((error) => {
                console.warn("[MobileTheme] Server save failed; local preference retained:", error);
            });
        }, 150);
    }, [walletAddress, deviceSessionToken]);
    const selectTheme = useCallback((nextTheme) => {
        localRevisionRef.current += 1;
        currentRef.current = { ...currentRef.current, theme: nextTheme };
        setTheme(nextTheme);
        writeStorage(THEME_STORAGE_KEY, nextTheme);
        queueServerSave(currentRef.current);
    }, [queueServerSave]);
    const setCustomColor = useCallback((color) => {
        const safeColor = clampCustomColor(color);
        localRevisionRef.current += 1;
        currentRef.current = { ...currentRef.current, customColor: safeColor };
        setCustomColorState(safeColor);
        writeStorage(CUSTOM_COLOR_STORAGE_KEY, safeColor);
        queueServerSave(currentRef.current);
    }, [queueServerSave]);
    useEffect(() => {
        if (!walletAddress || !deviceSessionToken)
            return;
        let cancelled = false;
        const revisionAtStart = localRevisionRef.current;
        void getMobileThemePreference(deviceSessionToken)
            .then((remote) => {
            if (cancelled)
                return;
            if (localRevisionRef.current !== revisionAtStart) {
                queueServerSave(currentRef.current);
                return;
            }
            if (!remote) {
                queueServerSave(currentRef.current);
                return;
            }
            const safeTheme = readValidTheme(remote.theme);
            const safeColor = clampCustomColor(remote.customColor);
            currentRef.current = { theme: safeTheme, customColor: safeColor };
            setTheme(safeTheme);
            setCustomColorState(safeColor);
            writeStorage(THEME_STORAGE_KEY, safeTheme);
            writeStorage(CUSTOM_COLOR_STORAGE_KEY, safeColor);
        })
            .catch((error) => {
            console.warn("[MobileTheme] Server load failed; using local preference:", error);
        });
        return () => {
            cancelled = true;
        };
    }, [walletAddress, deviceSessionToken, queueServerSave]);
    useEffect(() => () => {
        if (saveTimerRef.current)
            clearTimeout(saveTimerRef.current);
    }, []);
    const value = useMemo(() => ({ theme, customColor, selectTheme, setCustomColor }), [theme, customColor, selectTheme, setCustomColor]);
    return _jsx(MobileThemeContext.Provider, { value: value, children: children });
};
function readValidTheme(value) {
    return value === "black" || value === "cocoa" || value === "midnight-blue" || value === "custom"
        ? value
        : "black";
}
export function useMobileTheme() {
    const context = useContext(MobileThemeContext);
    if (!context) {
        throw new Error("useMobileTheme must be used inside MobileThemeProvider");
    }
    return context;
}
